import styled from '@mint-lib/styled';
import ListItem, {
  ListItemProps as ListItemPropsM,
} from '@mui/material/ListItem';

import Box from '../Box/Box.jsx';
import Icon from '../Icon/Icon.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import { processingStatusElements } from '../ProcessingModal/helpers.jsx';
import Tooltip, { TooltipProps as TooltipPropsM } from '../Tooltip/Tooltip.jsx';

export interface ProcessingModalItemProps extends ListItemPropsM {
  /**
   * The level can be from 1 to 5
   * @default false
   */
  level?: 0 | 1 | 2 | 3;
  /**
   * Processing Item Status
   */
  processingItemStatus?: 'default' | 'error' | 'success';
  /**
   * Extra styles are applied for the last level
   * @default false
   */
  levelIsLast?: boolean;
  /**
   *  if true shows chevron
   */
  expandable?: boolean;
  /**
   * callback for onClick on Expand Button
   */
  onExpandButtonClick?: () => void;
  /**
   * If item expanded - expand button changes icon
   */
  isItemExpanded?: boolean;
  /**
   *  Text to render tooltip for information
   */
  infoText?: string;
  /**
   * Tooltip props
   */
  TooltipProps?: Partial<TooltipPropsM>;
}

const ProcessingModalItem = ({
  level,
  processingItemStatus,
  levelIsLast,
  expandable,
  onExpandButtonClick,
  isItemExpanded,
  infoText,
  TooltipProps,
  ...props
}: ProcessingModalItemProps) => {
  return (
    <StyledListItem $level={level} $levelIsLast={levelIsLast} {...props}>
      <LeftContainer>
        {expandable ? (
          <IconButton
            variant="icon"
            size="small"
            icon={isItemExpanded ? 'ChevronDown' : 'ChevronRight'}
            onClick={onExpandButtonClick}
          />
        ) : null}

        {processingItemStatus ? (
          processingStatusElements[processingItemStatus]()
        ) : level ? (
          <Level $levelIsLast={levelIsLast} $level={level} />
        ) : null}
        {props.children}
      </LeftContainer>

      {infoText ? (
        <Box sx={{ height: '1rem' }}>
          <Tooltip arrow title={infoText} {...TooltipProps}>
            <InfoIconWrapper>
              <Icon component="Information" data-testid="information-icon" />
            </InfoIconWrapper>
          </Tooltip>
        </Box>
      ) : null}
    </StyledListItem>
  );
};

export default ProcessingModalItem;

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== '$levelIsLast' && prop !== '$level',
})<{
  $levelIsLast?: boolean;
  $level?: 0 | 1 | 2 | 3 | 4;
}>(({ theme, $levelIsLast, $level }) => ({
  ...theme.typography.bodyShort01,
  backgroundColor: theme.palette.ui.primary,
  verticalAlign: 'middle',
  justifyContent: 'space-between',
  height: '2rem',
  gap: '0.5rem',
  padding: '4px 12px 4px 8px',
  boxSizing: 'border-box',
  position: 'relative',
  '&::before': {
    content: $level ? "''" : null,
    width: 1,
    backgroundColor: theme.palette.ui.tertiary,
    position: 'absolute',
    height: $levelIsLast ? '50%' : '100%',
    left: 71.5,
    top: 0,
  },
}));
const LeftContainer = styled(Box)({
  height: '2rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '24rem',
});

const Level = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$levelIsLast' && prop !== '$level',
})<{
  $level?: 0 | 1 | 2 | 3;
  $levelIsLast?: boolean;
}>(({ theme, $level }) => ({
  position: 'relative',
  width: `${$level}rem`,
  height: 1,
  backgroundColor: theme.palette.ui.tertiary,
  marginLeft: '4rem',
}));
const InfoIconWrapper = styled('span')({
  width: '16px',
  height: '16px',
  textAlign: 'center',
});
